import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EntryCard from "@/components/feature/EntryCard";
import { categories } from "@/mocks/categories";
import { formatHot, getCategoryById, getEntriesByCategory, getHotEntries } from "@/hooks/useSiteData";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

type SortKey = "hot" | "fresh";

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = categoryId ? getCategoryById(categoryId) : undefined;
  const [sort, setSort] = useState<SortKey>("hot");

  const list = useMemo(() => {
    if (!category) return [];
    const items = getEntriesByCategory(category.id);
    return [...items].sort((a, b) =>
      sort === "hot" ? b.hot - a.hot : b.updatedAt.localeCompare(a.updatedAt),
    );
  }, [category, sort]);

  const hotList = getHotEntries(5);

  useSEO({
    title: category
      ? `${category.name}词条列表 - 黑料百科`
      : "词条分类 - 黑料百科",
    description: category
      ? `${category.desc} 共收录 ${list.length} 个${category.name}词条，按${sort === "hot" ? "热度" : "更新时间"}排序，含人物背景与事件时间线。`
      : "黑料百科词条分类，覆盖明星风波、网红观察、影视争议等话题方向。",
    keywords: category
      ? [category.name, "词条列表", "人物背景", "事件时间线"].join(",")
      : "词条分类,人物词条,明星词条,艺人档案",
    path: category ? `/category/${category.id}` : "/",
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}词条列表`,
            description: category.desc,
            url: `${SITE_ORIGIN}/category/${category.id}`,
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "黑料百科", url: SITE_ORIGIN },
            mainEntity: {
              "@type": "ItemList",
              name: `${category.name}词条`,
              itemListElement: list.map((entry, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: entry.title,
                url: `${SITE_ORIGIN}/entry/${entry.id}`,
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
              {
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: `${SITE_ORIGIN}/category/${category.id}`,
              },
            ],
          },
        ]
      : [],
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="px-4 py-24 text-center md:px-6">
          <i className="ri-layout-grid-line text-5xl text-foreground-300"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">可以返回首页看看其他栏目。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb items={[{ label: "词条分类", path: "/category/star" }, { label: category.name }]} />
      </div>

      <section className="border-b border-background-200 bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {category.name}词条
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{category.desc}</p>
            <p className="mt-3 text-xs text-foreground-500">
              当前收录 {list.length} 个词条 · 按{sort === "hot" ? "热度" : "最近更新"}排序
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">排序</span>
            <div className="flex rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setSort("hot")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                }`}
              >
                按热度
              </button>
              <button
                type="button"
                onClick={() => setSort("fresh")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  sort === "fresh" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                }`}
              >
                按更新
              </button>
            </div>
          </div>
        </div>

        <nav aria-label="分类切换" className="mt-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                cat.id === category.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={cat.icon}></i>
              {cat.name}
            </Link>
          ))}
        </nav>
      </section>

      <div className="grid grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {list.map((entry) => (
                <EntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-background-300 px-4 py-16 text-center text-sm text-foreground-500">
              该分类暂时还没有词条
            </p>
          )}
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">全站热度前五</h2>
            <ol className="mt-4 space-y-3">
              {hotList.map((item, index) => (
                <li key={item.id}>
                  <Link to={`/entry/${item.id}`} className="group flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] ${
                        index < 3 ? "bg-accent-100 text-accent-800" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="block text-xs text-foreground-500">{formatHot(item.hot)} 热度</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
            <Link
              to="/ranking"
              className="mt-5 inline-flex items-center gap-1 text-xs text-primary-700 transition-colors hover:text-primary-800"
            >
              查看完整榜单 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">其他分类</h2>
            <ul className="mt-4 space-y-2.5">
              {categories
                .filter((cat) => cat.id !== category.id)
                .map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                    >
                      <i className={`${cat.icon} text-accent-600`}></i>
                      {cat.name}
                      <span className="ml-auto text-xs text-foreground-400">{cat.entryCount}</span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}